<template>
  <div>
    <!-- Ancestor-only breadcrumb row (compact, when filters hide this node but it has matching descendants) -->
    <div
      v-if="isAncestorOnly"
      class="flex items-center px-4 py-1 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors"
      :style="{ paddingLeft: (depth * 24 + 16) + 'px' }"
      @click="$emit('select', node)"
    >
      <div class="w-4 h-4 shrink-0 mr-1.5" />
      <span
        class="text-[11px] font-medium px-1.5 py-0.5 rounded shrink-0 mr-3"
        :class="typeBadgeClass"
      >
        {{ node.requirement_type_name }}
      </span>
      <span class="text-xs text-gray-400 truncate">{{ node.title }}</span>
      <FontAwesomeIcon
        :icon="['fas', 'chevron-right']"
        class="text-[8px] text-gray-300 mx-2 shrink-0"
      />
    </div>

    <!-- Full row (normal display) -->
    <div
      v-else
      class="group flex items-center px-4 py-2.5 border-b border-gray-100 cursor-pointer transition-colors"
      :class="[
        node.id === selectedId ? 'bg-primary-50' : 'hover:bg-gray-50',
        isKeyboardFocused ? 'ring-1 ring-inset ring-primary-300' : '',
        isDone ? 'opacity-50' : '',
      ]"
      :style="{ paddingLeft: (depth * 24 + 16) + 'px' }"
      @click="$emit('select', node)"
    >
      <!-- Drag handle (visible on hover) -->
      <div
        class="drag-handle w-4 h-4 flex items-center justify-center shrink-0 mr-0.5 text-gray-300 hover:text-gray-500 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
        @click.stop
        @mousedown.stop
      >
        <FontAwesomeIcon :icon="['fas', 'grip-vertical']" class="text-[10px]" />
      </div>

      <!-- Expand/collapse toggle or empty spacer (same div either way so titles align) -->
      <div
        class="w-4 h-4 flex items-center justify-center shrink-0 mr-1.5"
        :class="hasChildren ? 'text-gray-400 hover:text-gray-600 cursor-pointer' : ''"
        @click.stop="hasChildren ? $emit('toggle', node.id) : null"
      >
        <FontAwesomeIcon
          v-if="hasChildren"
          :icon="['fas', 'chevron-right']"
          class="text-[10px] transition-transform"
          :class="{ 'rotate-90': isExpanded }"
        />
      </div>

      <!-- Checkbox for bulk select -->
      <input
        v-if="bulkMode"
        type="checkbox"
        :checked="isChecked"
        class="mr-2 shrink-0"
        @click.stop="$emit('toggle-check', node.id)"
      />

      <!-- Tenant (inline dropdown) -->
      <Support_InlineDropdown
        v-if="node.tenant_name"
        :model-value="node.tenant"
        :options="tenantDropdownOptions"
        :chip-style="tenantBadgeStyle"
        :display-label="node.tenant_name"
        class="mr-2"
        @update:model-value="updateField('tenant', $event)"
      />

      <!-- Type badge -->
      <span
        class="text-[11px] font-medium px-1.5 py-0.5 rounded shrink-0 mr-3"
        :class="typeBadgeClass"
      >
        {{ node.requirement_type_name }}
      </span>

      <!-- Title (display or edit) -->
      <div class="flex-1 min-w-0 mr-2 flex items-center gap-1 overflow-hidden">
        <input
          v-if="editingTitle"
          ref="titleInput"
          v-model="editValue"
          class="text-sm text-gray-900 w-full px-2 py-1 border border-primary-300 rounded bg-white outline-none ring-1 ring-primary-100"
          :class="{ 'font-semibold': depth === 0 }"
          @keydown.enter="saveTitle"
          @keydown.escape="cancelTitle"
          @blur="saveTitle"
          @click.stop
        />
        <span
          v-else
          class="text-sm text-gray-900 cursor-text rounded px-1 -mx-1 hover:bg-gray-100 hover:ring-1 hover:ring-gray-200 transition-colors shrink-0"
          :class="{ 'font-semibold': depth === 0, 'line-through': isDone }"
          @click.stop="startEditTitle"
        >
          {{ node.title }}
        </span>
        <span
          v-if="!node.customer_facing"
          class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0"
        >
          Internal
        </span>
        <template v-if="node.tags && node.tags.length">
          <span
            v-for="tag in node.tags.slice(0, 2)"
            :key="tag"
            class="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0"
          >
            {{ tag }}
          </span>
          <span
            v-if="node.tags.length > 2"
            class="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 shrink-0"
          >
            +{{ node.tags.length - 2 }}
          </span>
        </template>
      </div>

      <!-- Priority (inline dropdown) -->
      <Support_InlineDropdown
        v-if="node.priority && node.priority !== 'none'"
        :model-value="node.priority"
        :options="priorityOptions"
        :chip-class="priorityClass"
        :display-label="priorityLabel"
        @update:model-value="updateField('priority', $event)"
      />

      <!-- Status (inline dropdown) -->
      <Support_InlineDropdown
        :model-value="node.status"
        :options="statusOptions"
        :chip-class="statusClass"
        :display-label="statusLabel"
        class="ml-2"
        @update:model-value="updateField('status', $event)"
      />

      <!-- Owner (inline dropdown) -->
      <Support_InlineDropdown
        :model-value="node.owner"
        :options="ownerDropdownOptions"
        :display-label="node.owner_name || 'Unassigned'"
        chip-class="text-gray-500 bg-transparent hover:bg-gray-100"
        :align-right="true"
        class="mx-2 shrink-0"
        @update:model-value="updateField('owner', $event)"
      />

      <!-- Requester -->
      <span
        v-if="node.requester_name"
        class="text-[11px] text-gray-400 shrink-0 mr-2 max-w-[80px] truncate"
        :title="node.requester_name"
      >
        {{ node.requester_name }}
      </span>

      <!-- Comment count -->
      <span
        v-if="commentCounts[node.id]"
        class="inline-flex items-center gap-1 text-[11px] text-gray-400 shrink-0 mr-1"
      >
        <FontAwesomeIcon :icon="['far', 'comment']" class="text-[10px]" />
        {{ commentCounts[node.id] }}
      </span>

      <!-- Due date -->
      <span
        v-if="node.due_date"
        class="text-xs w-20 shrink-0 text-right"
        :class="dueDateDisplayClass"
      >
        {{ node.due_date }}
      </span>
      <span v-else class="w-20 shrink-0" />

      <!-- Quick-add button (visible on hover). Hidden on leaf types: with Bugs
           and Support Tickets in the same list as Epics, quick-add would
           otherwise file a Requirement under a Bug, which VALID_PARENT_TYPES
           refuses everywhere else. -->
      <button
        v-if="canAddChild"
        class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-primary-500 ml-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        @click.stop="$emit('quick-add', node)"
      >
        <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[10px]" />
      </button>
      <span v-else class="w-5 ml-1 shrink-0" />

      <!-- Actions menu (visible on hover) -->
      <button
        ref="actionsBtn"
        class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        @click.stop="$emit('actions-menu', { event: $event, node })"
      >
        <FontAwesomeIcon :icon="['fas', 'ellipsis']" class="text-xs" />
      </button>
    </div>

    <!-- Children: auto-expand when ancestor-only, otherwise respect expandedIds -->
    <draggable
      v-if="(isAncestorOnly || isExpanded) && node.children && node.children.length"
      :list="node.children"
      item-key="id"
      :group="{ name: 'requirements' }"
      handle=".drag-handle"
      ghost-class="drag-ghost"
      chosen-class="drag-chosen"
      :data-parent-id="node.id"
      :data-parent-title="node.title"
      @start="onDragStart"
      @end="onChildDragEnd"
    >
      <template #item="{ element }">
        <Support_Row
          :node="element"
          :depth="depth + 1"
          :selected-id="selectedId"
          :expanded-ids="expandedIds"
          :bulk-mode="bulkMode"
          :checked-ids="checkedIds"
          :keyboard-focused-id="keyboardFocusedId"
          :tenant-options="tenantOptions"
          :users="users"
          :comment-counts="commentCounts"
          :filter-match-ids="filterMatchIds"
          :has-active-filters="hasActiveFilters"
          @select="$emit('select', $event)"
          @toggle="$emit('toggle', $event)"
          @quick-add="$emit('quick-add', $event)"
          @toggle-check="$emit('toggle-check', $event)"
          @update-field="$emit('update-field', $event)"
          @update-title="$emit('update-title', $event)"
          @reorder="$emit('reorder', $event)"
          @reparent="$emit('reparent', $event)"
          @actions-menu="$emit('actions-menu', $event)"
        />
      </template>
    </draggable>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import draggable from 'vuedraggable';
import Support_InlineDropdown from './Support_InlineDropdown.vue';
import {
  CHILD_TYPES_FOR_PARENT,
  isSelectableStatus,
  daysUntil,
  isDueThisBusinessWeek,
  tenantColorFor,
} from './support_constants';

const STATUS_CLASSES = {
  draft: 'bg-gray-100 text-gray-600',
  approved: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
  open: 'bg-red-50 text-red-700',
  waiting: 'bg-orange-50 text-orange-700',
  resolved: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  draft: 'Draft',
  approved: 'Approved',
  in_progress: 'In Progress',
  done: 'Done',
  open: 'Open',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_CLASSES = {
  p0: 'bg-red-100 text-red-700',
  p1: 'bg-orange-100 text-orange-700',
  p2: 'bg-yellow-100 text-yellow-700',
  p3: 'bg-gray-100 text-gray-600',
};

const PRIORITY_LABELS = {
  p0: 'P0',
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
};

const TYPE_CLASSES = {
  'Epic': 'bg-indigo-50 text-indigo-700',
  'Feature': 'bg-blue-50 text-blue-700',
  'Requirement': 'bg-emerald-50 text-emerald-700',
  'Bug': 'bg-red-50 text-red-700',
  'Support Ticket': 'bg-amber-50 text-amber-700',
  'Fast-Follow': 'bg-violet-50 text-violet-700',
};

const DONE_STATUSES = new Set(['done', 'closed', 'resolved']);

export default {
  name: 'Support_Row',
  components: { FontAwesomeIcon, draggable, Support_InlineDropdown },
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    selectedId: { type: Number, default: null },
    expandedIds: { type: Set, default: () => new Set() },
    bulkMode: { type: Boolean, default: false },
    checkedIds: { type: Set, default: () => new Set() },
    keyboardFocusedId: { type: Number, default: null },
    tenantOptions: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    commentCounts: { type: Object, default: () => ({}) },
    filterMatchIds: { type: Set, default: () => new Set() },
    hasActiveFilters: { type: Boolean, default: false },
  },
  emits: ['select', 'toggle', 'quick-add', 'toggle-check', 'update-field', 'update-title', 'reorder', 'reparent', 'actions-menu'],
  data() {
    return {
      editingTitle: false,
      editValue: '',
    };
  },
  computed: {
    canAddChild() {
      return !!CHILD_TYPES_FOR_PARENT[this.node.requirement_type_name];
    },
    hasChildren() {
      return this.node.children && this.node.children.length > 0;
    },
    isExpanded() {
      return this.expandedIds.has(this.node.id);
    },
    isChecked() {
      return this.checkedIds.has(this.node.id);
    },
    isKeyboardFocused() {
      return this.keyboardFocusedId === this.node.id;
    },
    isAncestorOnly() {
      return this.hasActiveFilters && !this.filterMatchIds.has(this.node.id);
    },
    isDone() {
      return DONE_STATUSES.has(this.node.status);
    },
    statusClass() {
      return STATUS_CLASSES[this.node.status] || STATUS_CLASSES.draft;
    },
    statusLabel() {
      return STATUS_LABELS[this.node.status] || this.node.status;
    },
    priorityClass() {
      return PRIORITY_CLASSES[this.node.priority] || '';
    },
    priorityLabel() {
      return PRIORITY_LABELS[this.node.priority] || '';
    },
    typeBadgeClass() {
      return TYPE_CLASSES[this.node.requirement_type_name] || 'bg-gray-100 text-gray-600';
    },
    tenantBadgeStyle() {
      if (!this.node.tenant_name) return {};
      return tenantColorFor(this.node.tenant_name);
    },
    tenantDropdownOptions() {
      return this.tenantOptions.map(t => ({
        value: t.id,
        label: t.name,
        chipStyle: tenantColorFor(t.name),
      }));
    },
    isOverdue() {
      if (!this.node.due_date) return false;
      if (DONE_STATUSES.has(this.node.status)) return false;
      return new Date(this.node.due_date) < new Date();
    },
    dueDateDisplayClass() {
      if (!this.node.due_date || this.isDone) return 'text-gray-400';
      if (this.isOverdue) return 'text-red-500 font-medium';
      const diff = daysUntil(this.node.due_date);
      if (diff != null && diff <= 3) return 'text-amber-500 font-medium';
      if (isDueThisBusinessWeek(this.node.due_date))
        return 'text-yellow-500 font-medium';
      return 'text-gray-400';
    },
    statusOptions() {
      // 'resolved' is deprecated (Item 2): still rendered from STATUS_LABELS for
      // legacy rows, but not offered as a selectable option.
      return Object.entries(STATUS_LABELS)
        .filter(([value]) => isSelectableStatus(value))
        .map(([value, label]) => ({
          value,
          label,
          chipClass: STATUS_CLASSES[value],
        }));
    },
    priorityOptions() {
      return Object.entries(PRIORITY_LABELS).map(([value, label]) => ({
        value,
        label,
        chipClass: PRIORITY_CLASSES[value],
      }));
    },
    ownerDropdownOptions() {
      const options = [{ value: null, label: 'Unassigned', chipClass: 'text-gray-400' }];
      for (const u of this.users) {
        options.push({ value: u.id, label: u.full_name, chipClass: 'text-gray-700' });
      }
      return options;
    },
  },
  methods: {
    startEditTitle() {
      this.editingTitle = true;
      this.editValue = this.node.title;
      this.$nextTick(() => this.$refs.titleInput?.focus());
    },
    saveTitle() {
      if (!this.editingTitle) return;
      this.editingTitle = false;
      const trimmed = this.editValue.trim();
      if (trimmed && trimmed !== this.node.title) {
        this.$emit('update-title', { id: this.node.id, title: trimmed });
      }
    },
    cancelTitle() {
      this.editingTitle = false;
      this.editValue = '';
    },
    updateField(field, value) {
      this.$emit('update-field', { id: this.node.id, [field]: value });
    },
    onDragStart() {
      document.body.classList.add('is-dragging-requirement');
    },
    onChildDragEnd(evt) {
      document.body.classList.remove('is-dragging-requirement');
      const fromParentId = evt.from.dataset.parentId;
      const toParentId = evt.to.dataset.parentId;
      const itemId = this.node.children[evt.newIndex]?.id
        || parseInt(evt.item.dataset.id);

      if (fromParentId !== toParentId) {
        // Moved to a different parent — emit reparent
        this.$emit('reparent', {
          id: itemId,
          newParentId: toParentId ? Number(toParentId) : null,
          newIndex: evt.newIndex,
        });
      } else {
        // Same parent — just reorder
        const items = this.node.children.map((child, index) => ({
          id: child.id,
          position: index,
        }));
        this.$emit('reorder', items);
      }
    },
  },
};
</script>

<style>
.drag-chosen {
  opacity: 0.9;
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.12);
  background: white;
  border-radius: 0.25rem;
}

.drag-ghost {
  opacity: 0.35;
  background: #fef3f0;
  border: 2px dashed #f7967a;
  border-radius: 0.25rem;
}

.drag-ghost > * {
  visibility: hidden;
}

body.is-dragging-requirement [data-parent-id] {
  transition: background-color 0.15s, outline 0.15s;
  border-radius: 0.25rem;
}

body.is-dragging-requirement [data-parent-id]:has(.drag-ghost) {
  background-color: #fef3f0;
  outline: 2px dashed #f7967a;
  outline-offset: -2px;
}
</style>
